import type { ResponseMetadata } from '@delivery-metrics/shared';
import {
  CheckCircle,
  Error as ErrorIcon,
  Help,
  Warning,
} from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import React from 'react';

import { describeFreshness, type FreshnessSeverity } from '../utils/freshness';

export interface FreshnessIndicatorProps {
  /**
   * `metadata` from any v2 response. `null` while it is still being fetched,
   * or when the request failed — both of which the indicator says out loud
   * rather than falling back to a reassuring default.
   */
  metadata: ResponseMetadata | null | undefined;
  /** Suppresses the chip entirely while the first request is in flight. */
  isLoading?: boolean;
}

const CHIP_COLOR: Record<
  FreshnessSeverity,
  'default' | 'error' | 'warning' | 'success'
> = {
  unknown: 'default',
  stale: 'error',
  partial: 'warning',
  fresh: 'success',
};

const CHIP_ICON: Record<FreshnessSeverity, React.ReactElement> = {
  unknown: <Help />,
  stale: <ErrorIcon />,
  partial: <Warning />,
  fresh: <CheckCircle />,
};

/**
 * How old the data on screen is, from the server rather than from the clock
 * (US-23).
 *
 * It is always rendered once loaded, including when everything is fine. The
 * state it exists for is `partial` — a cycle where one source failed — and a
 * badge that appears only on bad news is a badge whose absence means nothing.
 */
export const FreshnessIndicator: React.FC<FreshnessIndicatorProps> = ({
  metadata,
  isLoading = false,
}) => {
  if (isLoading) {
    return null;
  }

  const { severity, label, detail } = describeFreshness(metadata);

  return (
    <Tooltip title={detail}>
      <Chip
        icon={CHIP_ICON[severity]}
        label={label}
        color={CHIP_COLOR[severity]}
        variant={severity === 'fresh' ? 'outlined' : 'filled'}
        size='small'
        data-testid='freshness-indicator'
        data-severity={severity}
      />
    </Tooltip>
  );
};
